import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Lock, User, Server, Activity, ChevronRight, Cpu, Shield, Database, Network } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [validationErrors, setValidationErrors] = useState({ email: '', password: '' });
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [systemStatus, setSystemStatus] = useState({
    securityModules: Math.floor(Math.random() * 10) + 90, // 90-99%
    databaseConnection: Math.floor(Math.random() * 5) + 95, // 95-99%
    apiServers: Math.floor(Math.random() * 5) + 95, // 95-99%
    lastScan: new Date().toLocaleTimeString()
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, error: authError } = useAuth();
  
  // If user is already authenticated, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      // Fix: Use a properly formed path instead of potentially trying to construct a URL
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);
  
  // Simulate periodic system status updates
  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStatus(prev => ({
        securityModules: Math.min(99, Math.max(90, prev.securityModules + (Math.random() > 0.5 ? 1 : -1))),
        databaseConnection: Math.min(99, Math.max(95, prev.databaseConnection + (Math.random() > 0.5 ? 0.5 : -0.5))),
        apiServers: Math.min(99, Math.max(95, prev.apiServers + (Math.random() > 0.5 ? 0.5 : -0.5))),
        lastScan: new Date().toLocaleTimeString()
      }));
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Set default email from environment variable if available
  useEffect(() => {
    const defaultEmail = import.meta.env.VITE_ADMIN_EMAIL;
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, []);
  
  const validateForm = () => {
    let isValid = true;
    const errors = { email: '', password: '' };
    
    // Email validation
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email format is invalid';
      isValid = false;
    }
    
    // Password validation
    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoginStatus('loading');
    setLoginAttempts(prev => prev + 1);
    
    try {
      const success = await login(email, password);
      if (success) {
        setLoginStatus('success');
        // Redirect handled by the useEffect above
      } else {
        setLoginStatus('error');
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginStatus('error');
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-x-hidden">
      {/* Background Video with Blur Effect */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 blur-sm"
        style={{ filter: 'blur(4px) brightness(0.3)' }}
      >
        <source src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/background-cc.mp4" type="video/mp4" />
      </video>
      
      {/* Dynamic Cyber Threat Map Background */}
      <div className="absolute inset-0 z-0 opacity-40">
        <div className="w-full h-full bg-slate-800"></div>
      </div>
      
      {/* Glowing Teal Accent */}
      <div className="fixed -top-20 -right-20 w-96 h-96 bg-teal-900/20 rounded-full blur-[100px] z-0"></div>
      <div className="fixed top-1/3 -left-20 w-80 h-80 bg-cyan-900/10 rounded-full blur-[100px] z-0"></div>
      
      {/* Grid Background */}
      <div className="fixed inset-0 z-0 opacity-10 animated-grid"></div>

      {/* Animated Security Scan Line */}
      <div className="fixed inset-0 z-0">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-teal-500 to-transparent absolute"
          style={{ 
            animation: 'scanVertical 8s infinite linear',
            boxShadow: '0 0 15px rgba(20, 184, 166, 0.5), 0 0 30px rgba(20, 184, 166, 0.3)'
          }}>
        </div>
        <div className="w-px h-full bg-gradient-to-b from-transparent via-teal-500 to-transparent absolute"
          style={{ 
            animation: 'scanHorizontal 10s infinite linear',
            boxShadow: '0 0 15px rgba(20, 184, 166, 0.5), 0 0 30px rgba(20, 184, 166, 0.3)'
          }}>
        </div>
      </div>
      
      {/* Login Container */}
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Logo and Header */}
          <div className="text-center mb-6 sm:mb-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-teal-900/20 border border-teal-700/30 mb-4 relative"
            >
              <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-10 w-10 sm:h-12 sm:w-12" />
              {/* Animated ping effect */}
              <span className="absolute h-full w-full rounded-full border-2 border-teal-500/30 animate-ping"></span>
            </motion.div>
            
            <motion.h2
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-white"
            >
              AbhiStrike <span className="text-teal-400">Command Center</span>
            </motion.h2>
            
            <motion.p
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-2 text-xs sm:text-sm text-teal-400"
            >
              Advanced AI SIEM & EDR Threat Monitoring & Protection.
            </motion.p>
          </div>
          
          {/* Security Status Indicator */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-slate-800/40 backdrop-blur-sm rounded-lg border border-teal-900/30 p-3 mb-4 sm:mb-6"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-4 w-4 mr-2" />
                <span className="text-xs sm:text-sm font-medium">System Security Status</span>
              </div>
              <div className="flex items-center">
                <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse mr-1"></span>
                <span className="text-xs text-emerald-400">OPERATIONAL</span>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-slate-900/30 rounded p-2 border border-slate-700/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Shield className="h-3 w-3 mr-1 text-teal-400" />
                    Security
                  </span>
                  <span className="text-emerald-400">{systemStatus.securityModules}%</span>
                </div>
                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-teal-600 to-teal-500 rounded-full" 
                    style={{ width: `${systemStatus.securityModules}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-slate-900/30 rounded p-2 border border-slate-700/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Database className="h-3 w-3 mr-1 text-teal-400" />
                    Database
                  </span>
                  <span className="text-emerald-400">{systemStatus.databaseConnection}%</span>
                </div>
                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-teal-600 to-teal-500 rounded-full" 
                    style={{ width: `${systemStatus.databaseConnection}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="bg-slate-900/30 rounded p-2 border border-slate-700/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-400 flex items-center">
                    <Network className="h-3 w-3 mr-1 text-teal-400" />
                    API
                  </span>
                  <span className="text-emerald-400">{systemStatus.apiServers}%</span>
                </div>
                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-teal-600 to-teal-500 rounded-full" 
                    style={{ width: `${systemStatus.apiServers}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="mt-2 text-center text-xs text-slate-500">
              <span>Last security scan: {systemStatus.lastScan}</span>
            </div>
          </motion.div>
          
          {/* Login Form */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-slate-800/60 backdrop-blur-md rounded-lg border border-slate-700 overflow-hidden"
          >
            <div className="p-4 sm:p-6">
              <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
                {/* System Access Alert */}
                <div className="bg-cyan-900/10 border border-cyan-900/30 rounded-md p-2 sm:p-3 text-xs sm:text-sm mb-3 sm:mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-xs sm:text-sm font-medium text-cyan-500">Authorized Access Only</h3>
                      <div className="mt-1 text-xs text-slate-400">
                        <p>This system is restricted to authorized personnel only. All access attempts are logged and monitored.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-slate-400 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-2 sm:py-2.5 bg-slate-900/50 border ${validationErrors.email ? 'border-cyan-600' : 'border-slate-600'} rounded-md shadow-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 sm:text-sm text-white`}
                      placeholder="example@abhi.co"
                    />
                  </div>
                  {validationErrors.email && (
                    <p className="mt-1 text-xs text-cyan-500">{validationErrors.email}</p>
                  )}
                </div>
                
                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-slate-400 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`block w-full pl-10 pr-10 py-2 sm:py-2.5 bg-slate-900/50 border ${validationErrors.password ? 'border-cyan-600' : 'border-slate-600'} rounded-md shadow-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 sm:text-sm text-white`}
                      placeholder="••••••••••••••••"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-slate-500 hover:text-white focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                        ) : (
                          <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  {validationErrors.password && (
                    <p className="mt-1 text-xs text-cyan-500">{validationErrors.password}</p>
                  )}
                </div>
                
                {/* Login Error Message */}
                {(loginStatus === 'error' || authError) && (
                  <div className="bg-cyan-900/20 border border-cyan-900/40 rounded-md p-3 flex items-start">
                    <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500 mr-2 mt-0.5" />
                    <div>
                      <p className="text-xs sm:text-sm text-cyan-500 font-medium">Authentication Failed</p>
                      <p className="text-xs text-slate-400">
                        {authError || "Invalid email or password. Please try again."}
                        {loginAttempts >= 3 && " For assistance, contact security administrators."}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Login Button */}
                <div>
                  <button
                    type="submit"
                    disabled={loginStatus === 'loading'}
                    className={`w-full flex items-center justify-center px-4 py-2 sm:py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      loginStatus === 'loading' 
                        ? 'bg-slate-700 cursor-not-allowed' 
                        : 'bg-teal-700 hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
                    } transition-colors duration-200`}
                  >
                    {loginStatus === 'loading' ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Authenticating...
                      </>
                    ) : (
                      <>
                        <span>Secure Login</span>
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
            
            {/* Terminal-like Footer */}
            <div className="flex items-center justify-between bg-slate-900/80 px-3 sm:px-4 py-2 border-t border-slate-700/60 text-xs text-slate-500 font-mono">
              <div className="flex items-center">
                <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Security" className="h-3 w-3 mr-1" />
                <span>ABHI AI SECURITY SYSTEM</span>
              </div>
              <div className="flex items-center">
                <div className="h-1.5 w-1.5 bg-emerald-500 rounded-full mr-1"></div>
                <span>256-BIT ENCRYPTION</span>
              </div>
            </div>
          </motion.div>
          
          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-4 sm:mt-6 text-center text-xs text-slate-500"
          >
            <p>Abhi Pvt Ltd © 2025. Advanced AI SIEM & EDR & Dark Web Threat Detection.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;